import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import QuickViewStats from './QuickViewStats';
import AdvancedAnalytics from './AdvancedAnalytics';
import TablesManagement from './TablesManagement';
import UsersManagement from './UsersManagement';
// eslint-disable-next-line no-unused-vars
import AdminActions from './AdminActions';
import InventoryManagement from './InventoryManagement';
import ProductManagement from './ProductManagement';
import ThemeManagement from './ThemeManagement';
// eslint-disable-next-line no-unused-vars
import Projections from './Projections';
import IEPSCalculator from './IEPSCalculator';
import SpecialsManagement from './SpecialsManagement';
import RegisterManagement from './Register/RegisterManagement';
import TipAnalysisModal from './TipAnalysisModal';
import OptionManagement from './OptionManagement';
import InvoiceGenerator from './InvoiceGenerator';
import OutboundInvoices from './OutboundInvoices';
import InboundInvoices from './InboundInvoices';
import BeerProduction from './BeerProduction';
import PrinterManagement from './PrinterManagement';
import SystemSettings from './SystemSettings';
import DigitalMenuSettings from './DigitalMenuSettings';
import ActiveSessionsManagement from './ActiveSessionsManagement';
import AdminLayoutConfigurator, { DEFAULT_LAYOUT } from './AdminLayoutConfigurator';
import { useLanguage } from '../contexts/LanguageContext';
import { useSystemFeatures } from '../hooks/useSystemFeatures';
import { bucketRevenueByTender } from '../utils/paymentMethods';

// Defined outside component to prevent remount on every render
const CollapsibleSection = ({ title, isOpen, onToggle, theme, children }) => (
    <div style={{
        background: theme?.colors?.surface || '#fff',
        borderRadius: theme?.borderRadius || 8,
        boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)',
        marginBottom: '1.5rem',
        overflow: 'hidden'
    }}>
        <div
            onClick={onToggle}
            onTouchEnd={(e) => {
                e.preventDefault();
                onToggle();
            }}
            style={{
                padding: '.5rem 1.5rem',
                backgroundColor: theme?.colors?.primary || '#1976d2',
                color: theme?.colors?.surface || '#fff',
                cursor: 'pointer',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontWeight: 'bold',
                fontSize: '1.1rem',
                WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                userSelect: 'none'
            }}
        >
            <span>{title}</span>
            <span style={{ fontSize: '1.2rem' }}>
                {isOpen ? '▼' : '▶'}
            </span>
        </div>
        {isOpen && (
            <div style={{ padding: '0' }}>
                {children}
            </div>
        )}
    </div>
);

export default function AdminDashboard({ theme, onThemeChange, onBrandingChange, user }) {
    const { t } = useLanguage();
    const { tableAreasEnabled } = useSystemFeatures();
    const [searchParams, setSearchParams] = useSearchParams();

    // Admin layout state (loaded from system_settings, falls back to DEFAULT_LAYOUT)
    const [adminLayout, setAdminLayout] = useState(DEFAULT_LAYOUT);
    const [showAdminLayout, setShowAdminLayout] = useState(false);

    // Derive visible tabs from layout
    const TAB_CONFIG = adminLayout.tabs
        .filter(tab => tab.visible)
        .map(tab => ({ id: tab.id, icon: tab.icon, labelKey: tab.labelKey }));

    // Tab state management with URL persistence (default to 'setup' tab)
    const activeTab = searchParams.get('tab') || localStorage.getItem('adminDashboard_activeTab') || 'setup';

    const handleTabChange = (tabId) => {
        setSearchParams({ tab: tabId });
        localStorage.setItem('adminDashboard_activeTab', tabId);
    };

    // (already declared below with other section visibility states)
    const [stats, setStats] = useState({
        totalOrders: 0,
        openRevenue: 0,
        closedRevenue: 0,
        totalRevenue: 0,
        openOrders: 0,
        closedOrders: 0,
        cashRevenue: 0,
        creditRevenue: 0,
        otherRevenue: 0
    });
    const [tables, setTables] = useState([]);
    const [users, setUsers] = useState([]);
    const [userRoles, setUserRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [timePeriod, setTimePeriod] = useState('week');
    const [selectedDate, setSelectedDate] = useState(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    });
    const [selectedHour, setSelectedHour] = useState(() => new Date().getHours());
    const [showAddTable, setShowAddTable] = useState(false);
    const [showAddUser, setShowAddUser] = useState(false);
    const [editingTable, setEditingTable] = useState(null);
    const [editingUser, setEditingUser] = useState(null);
    const [showDeletedTables, setShowDeletedTables] = useState(false);
    const [showInactiveUsers, setShowInactiveUsers] = useState(false);
    const [showOpenTabs, setShowOpenTabs] = useState(false);
    const [showClosedTabs, setShowClosedTabs] = useState(false);
    const [showTotalTabs, setShowTotalTabs] = useState(false);
    const [showCashTabs, setShowCashTabs] = useState(false);
    const [showCreditTabs, setShowCreditTabs] = useState(false);
    const [showOtherTabs, setShowOtherTabs] = useState(false);
    const [showAverageTimesTabs, setShowAverageTimesTabs] = useState(false);
    const [openOrders, setOpenOrders] = useState([]);
    const [closedOrders, setClosedOrders] = useState([]);
    const [totalOrders, setTotalOrders] = useState([]);
    const [cashOrders, setCashOrders] = useState([]);
    const [creditOrders, setCreditOrders] = useState([]);
    const [otherOrders, setOtherOrders] = useState([]);
    const [averageTimesByArea, setAverageTimesByArea] = useState([]);

    // Section visibility states
    const [showQuickStats, setShowQuickStats] = useState(false);
    const [showAdvancedAnalytics, setShowAdvancedAnalytics] = useState(false);
    const [showTablesManagement, setShowTablesManagement] = useState(false);
    const [showUsersManagement, setShowUsersManagement] = useState(false);
    const [showRegisterManagement, setShowRegisterManagement] = useState(false);
    const [showInventoryManagement, setShowInventoryManagement] = useState(false);
    const [showProductManagement, setShowProductManagement] = useState(false);
    const [showThemeManagement, setShowThemeManagement] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [showProjections, setShowProjections] = useState(false);
    const [showIEPSCalculator, setShowIEPSCalculator] = useState(false);
    const [showSpecialsManagement, setShowSpecialsManagement] = useState(false);
    const [showTipAnalysis, setShowTipAnalysis] = useState(false);
    const [showOptionManagement, setShowOptionManagement] = useState(false);
    const [showInvoiceGenerator, setShowInvoiceGenerator] = useState(false);
    const [selectedOrderForInvoice, setSelectedOrderForInvoice] = useState(null);
    const [showInvoiceHistory, setShowInvoiceHistory] = useState(false);
    const [showInboundInvoices, setShowInboundInvoices] = useState(false);
    const [showBeerProduction, setShowBeerProduction] = useState(false);
    const [showPrinterManagement, setShowPrinterManagement] = useState(false);
    const [showSystemSettings, setShowSystemSettings] = useState(false);
    const [showActiveSessionsManagement, setShowActiveSessionsManagement] = useState(false);
    const [floorPlanSettings, setFloorPlanSettings] = useState({ floorPlanWidth: 600, floorPlanHeight: 400, gridSnapSize: 0 });

    const [tableAreas, setTableAreas] = useState([]);

    // Form states for adding/editing
    const [tableForm, setTableForm] = useState({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
    const [userForm, setUserForm] = useState({ email: '', password: '', user_name: '', first_names: '', last_names: '', cell_phone: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
    const [changePassword, setChangePassword] = useState(false);
    const [changePin, setChangePin] = useState(false);

    const fetchAdminData = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            // Fetch orders data (use admin endpoint to get all orders)
            let orderUrl = timePeriod === 'all'
                ? `${BACKEND_URL}/api/admin/orders`
                : `${BACKEND_URL}/api/admin/orders?period=${timePeriod}`;

            // Add selectedDate parameter for time periods that use it
            if (timePeriod !== 'all' && selectedDate) {
                orderUrl += `&date=${selectedDate}`;
            }

            // Add hour parameter for hourly view
            if (timePeriod === 'hour' && selectedHour !== undefined) {
                orderUrl += `&hour=${selectedHour}`;
            }

            const ordersResponse = await fetch(orderUrl);
            let orders = [];
            if (ordersResponse.ok) {
                orders = await ordersResponse.json();

                                // Deduplicate orders by id
                const uniqueOrdersMap = new Map();
                for (let i = orders.length - 1; i >= 0; i--) {
                    const order = orders[i];
                    uniqueOrdersMap.set(order.id, order);
                }
                const uniqueOrders = Array.from(uniqueOrdersMap.values());
                orders = uniqueOrders;
                // Separate open and closed orders
                const openOrdersList = orders.filter(order => !order.closed);
                const closedOrdersList = orders.filter(order => order.closed);

                // Store the order lists
                setOpenOrders(openOrdersList);
                setClosedOrders(closedOrdersList);
                setTotalOrders([...openOrdersList, ...closedOrdersList]);

                // Separate closed orders by what was actually tendered. An order
                // paid with more than one method is in every list it paid into,
                // and each list counts only that method's share of it.
                const tenderBuckets = bucketRevenueByTender(closedOrdersList);

                setCashOrders(tenderBuckets.orders.cash);
                setCreditOrders(tenderBuckets.orders.card);
                setOtherOrders(tenderBuckets.orders.other);

                // Calculate revenue for open checks (excluding tips)
                const openRevenue = openOrdersList.reduce((sum, order) => {
                    const orderRevenue = Number(order.order_revenue) || 0;
                    return sum + orderRevenue;
                }, 0);

                // Calculate revenue for closed checks (excluding tips)
                const closedRevenue = closedOrdersList.reduce((sum, order) => {
                    const orderRevenue = Number(order.order_revenue) || 0;
                    return sum + orderRevenue;
                }, 0);

                // Total revenue (open + closed)
                const totalRevenue = openRevenue + closedRevenue;

                // Revenue by payment method for closed orders only (excluding
                // tips), summed over the tenders rather than over the order's
                // single payment type: that column names the largest tender, so
                // reading it would report a 600 cash plus 400 card split as
                // 1000 cash.
                const cashRevenue = tenderBuckets.revenue.cash;
                const creditRevenue = tenderBuckets.revenue.card;
                const otherRevenue = tenderBuckets.revenue.other;

                // Calculate average order total
                const avgOrderTotal = orders.length > 0 ? totalRevenue / orders.length : 0;

                setStats({
                    totalOrders: orders.length,
                    openRevenue,
                    closedRevenue,
                    totalRevenue,
                    openOrders: openOrdersList.length,
                    closedOrders: closedOrdersList.length,
                    cashRevenue,
                    creditRevenue,
                    otherRevenue,
                    avgOrderTotal
                });
            } else {
                console.warn('Failed to fetch orders:', ordersResponse.status);
            }

            // Fetch tables data
            const tablesResponse = await fetch(`${BACKEND_URL}/api/tables`);
            if (tablesResponse.ok) {
                const tablesData = await tablesResponse.json();
                // Filter to only show non-temporary tables, and optionally filter deleted tables
                const filteredTables = (tablesData || []).filter(table => {
                    if (table.temporary) return false; // Always exclude temporary tables
                    if (!showDeletedTables && table.deleted) return false; // Optionally exclude deleted tables
                    return true;
                });

                // Sort tables by numeric name (1, 2, 3, 10, 11, 20, etc.)
                const sortedTables = filteredTables.sort((a, b) => {
                    const aNum = parseInt(a.name) || 0;
                    const bNum = parseInt(b.name) || 0;

                    // If both are numbers, sort numerically
                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return aNum - bNum;
                    }

                    // If one is a number and one isn't, number comes first
                    if (!isNaN(aNum) && isNaN(bNum)) return -1;
                    if (isNaN(aNum) && !isNaN(bNum)) return 1;

                    // If neither is a number, sort alphabetically
                    return a.name.localeCompare(b.name);
                });

                setTables(sortedTables);
            } else {
                console.warn('Failed to fetch tables:', tablesResponse.status);
            }

            // Fetch user data from admin endpoint
            try {
                const usersResponse = await fetch(`${BACKEND_URL}/api/admin/users`);
                if (usersResponse.ok) {
                    const userData = await usersResponse.json();
                    const allUsers = Array.isArray(userData) ? userData : [userData];
                    // Filter users based on showInactiveUsers toggle
                    const filteredUsers = showInactiveUsers ? allUsers : allUsers.filter(user => user.active);
                    setUsers(filteredUsers);
                } else {
                    // Set placeholder data since /api/users doesn't exist
                    setUsers([
                        { id: 1, email: 'admin@system', user_name: 'admin', active: 1, created: new Date().toISOString() },
                        { id: 2, email: 'server@system', user_name: 'server', active: 1, created: new Date().toISOString() }
                    ]);
                }
            } catch (userErr) {
                console.warn('Users endpoint not available:', userErr.message);
                setUsers([
                    { id: 1, email: 'admin@system', user_name: 'admin', active: 1, created: new Date().toISOString() },
                    { id: 2, email: 'server@system', user_name: 'server', active: 1, created: new Date().toISOString() }
                ]);
            }

            // Fetch user roles
            try {
                const rolesResponse = await fetch(`${BACKEND_URL}/api/user-roles`);
                if (rolesResponse.ok) {
                    const rolesData = await rolesResponse.json();
                    setUserRoles(rolesData);
                } else {
                    console.warn('Failed to fetch user roles:', rolesResponse.status);
                }
            } catch (roleErr) {
                console.warn('User roles endpoint not available:', roleErr.message);
                setUserRoles([
                    { id: 1, title: 'Manager' },
                    { id: 2, title: 'Server' },
                    { id: 3, title: 'Bar' },
                    { id: 4, title: 'Owner' },
                    { id: 5, title: 'Kitchen' }
                ]);
            }

            // Fetch average times by area
            try {
                let averageTimesUrl = timePeriod === 'all'
                    ? `${BACKEND_URL}/api/admin/average-times-by-area`
                    : `${BACKEND_URL}/api/admin/average-times-by-area?period=${timePeriod}`;

                // Add selectedDate parameter for time periods that use it
                if (timePeriod !== 'all' && selectedDate) {
                    averageTimesUrl += `&date=${selectedDate}`;
                }

                // Add hour parameter for hourly view
                if (timePeriod === 'hour' && selectedHour !== undefined) {
                    averageTimesUrl += `&hour=${selectedHour}`;
                }

                const averageTimesResponse = await fetch(averageTimesUrl);
                if (averageTimesResponse.ok) {
                    const averageTimesData = await averageTimesResponse.json();
                    setAverageTimesByArea(averageTimesData);
                } else {
                    console.warn('Failed to fetch average times by area:', averageTimesResponse.status);
                    setAverageTimesByArea([]);
                }
            } catch (avgTimesErr) {
                console.warn('Average times by area endpoint not available:', avgTimesErr.message);
                setAverageTimesByArea([]);
            }

        } catch (err) {
            console.error('Error fetching admin data:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [timePeriod, selectedDate, selectedHour, showDeletedTables, showInactiveUsers]);

    // Re-fetch data when time period, date, hour, or filters change
    useEffect(() => {
        fetchAdminData();
    }, [fetchAdminData]);

    // Fetch floor plan settings and admin layout
    useEffect(() => {
        async function fetchSettings() {
            try {
                const response = await fetch(`${BACKEND_URL}/api/system-settings`);
                if (response.ok) {
                    const data = await response.json();
                    setFloorPlanSettings({
                        floorPlanWidth: parseInt(data.floorPlanWidth) || 600,
                        floorPlanHeight: parseInt(data.floorPlanHeight) || 400,
                        gridSnapSize: parseInt(data.gridSnapSize) || 0
                    });
                    // Load admin layout
                    if (data.adminLayout) {
                        try {
                            const parsed = typeof data.adminLayout === 'string' ? JSON.parse(data.adminLayout) : data.adminLayout;
                            if (parsed && parsed.tabs) setAdminLayout(parsed);
                        } catch { /* keep default */ }
                    }
                }
            } catch (err) {
                console.error('Error fetching settings:', err);
            }
        }
        fetchSettings();
    }, []);

    const handleSaveFloorPlanSettings = async (newSettings) => {
        // Filter out empty/invalid values — only save valid numbers
        const validSettings = Object.fromEntries(
            Object.entries(newSettings).filter(([, v]) => v !== '' && v !== null && v !== undefined && !isNaN(v))
        );
        if (Object.keys(validSettings).length === 0) return;
        setFloorPlanSettings(prev => ({ ...prev, ...validSettings }));
        try {
            await fetch(`${BACKEND_URL}/api/system-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(Object.fromEntries(
                    Object.entries(validSettings).map(([k, v]) => [k, String(v)])
                ))
            });
        } catch (err) {
            console.error('Error saving floor plan settings:', err);
        }
    };

    // Areas are only offered where the tenant has the feature on
    const fetchTableAreas = useCallback(async () => {
        if (!tableAreasEnabled) return;
        try {
            const response = await fetch(`${BACKEND_URL}/api/table-areas`);
            if (!response.ok) return;
            const data = await response.json();
            setTableAreas(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching table areas:', err);
        }
    }, [tableAreasEnabled]);

    useEffect(() => {
        fetchTableAreas();
    }, [fetchTableAreas]);

    const handleDeleteTable = async (tableId) => {
        if (!window.confirm('Are you sure you want to delete this table?')) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/tables/${tableId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete table');
            }

            // Refresh tables data
            fetchAdminData();
            alert('Table deleted successfully');
        } catch (err) {
            console.error('Error deleting table:', err);
            alert('Failed to delete table: ' + err.message);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Are you sure you want to delete this user?')) return;

        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error('Failed to delete user');
            }

            // Refresh users data
            fetchAdminData();
            alert(t('userDeleted'));
        } catch (err) {
            console.error('Error deleting user:', err);
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleAddTable = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/tables`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: tableForm.name,
                    shape: tableForm.shape,
                    width: tableForm.width,
                    height: tableForm.height,
                    restaurant_id: 1,
                    area_id: tableForm.area_id || 1,
                    x: 100,
                    y: 100
                })
            });

            if (!response.ok) {
                throw new Error(t('error') + ': Failed to add table');
            }

            setShowAddTable(false);
            setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
            fetchAdminData();
            alert(t('tableCreated'));
        } catch (err) {
            console.error('Error adding table:', err);
            alert(t('error') + ': ' + err.message);
        }
    };

    const handleEditTable = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/tables/${editingTable.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: tableForm.name,
                    shape: tableForm.shape,
                    x: editingTable.x,
                    y: editingTable.y,
                    width: tableForm.width,
                    height: tableForm.height,
                    area_id: tableForm.area_id || 1,
                    deleted: tableForm.deleted !== undefined ? tableForm.deleted : editingTable.deleted
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update table');
            }

            setEditingTable(null);
            setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
            fetchAdminData();
            alert('Table updated successfully');
        } catch (err) {
            console.error('Error updating table:', err);
            alert('Failed to update table: ' + err.message);
        }
    };

    const handleAddUser = async () => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/admin/users`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(userForm)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to add user');
            }

            setShowAddUser(false);
            setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', cell_phone: '', active: 1, user_role: 2 });
            fetchAdminData();
            alert('User added successfully');
        } catch (err) {
            console.error('Error adding user:', err);
            alert('Failed to add user: ' + err.message);
        }
    };

    const handleEditUser = async () => {
        try {
            // Validate PIN if changing it
            if (changePin) {
                if (!userForm.pin || userForm.pin.length !== 4 || !/^\d{4}$/.test(userForm.pin)) {
                    alert(t('pin') + ' ' + t('mustBe4Digits') || 'PIN must be exactly 4 digits');
                    return;
                }
                if (userForm.pin !== userForm.confirmPin) {
                    alert(t('pinsDoNotMatch'));
                    return;
                }
            }

            const updateData = {
                email: userForm.email,
                active: userForm.active,
                user_name: userForm.user_name,
                first_names: userForm.first_names,
                last_names: userForm.last_names,
                cell_phone: userForm.cell_phone,
                user_role: userForm.user_role
            };

            // Only include password if changing it
            if (changePassword && userForm.password) {
                updateData.password = userForm.password;
            }

            const response = await fetch(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(updateData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to update user');
            }

            // Update PIN separately if changing it
            if (changePin && userForm.pin) {
                const pinResponse = await fetch(`${BACKEND_URL}/api/user/${editingUser.id}/pin`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pin: userForm.pin })
                });

                if (!pinResponse.ok) {
                    const pinError = await pinResponse.json();
                    throw new Error(pinError.error || 'Failed to update PIN');
                }
            }

            setEditingUser(null);
            setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
            setChangePassword(false);
            setChangePin(false);
            fetchAdminData();
            alert(t('userUpdated') || 'User updated successfully');
        } catch (err) {
            console.error('Error updating user:', err);
            alert(t('error') + ': ' + err.message);
        }
    };

    // Handle opening edit forms
    const handleOpenEditTable = (table) => {
        setTableForm({
            name: table.name,
            shape: table.shape || 'rectangle',
            width: table.width || 80,
            height: table.height || 50,
            area_id: table.area_id || 1,
            deleted: table.deleted
        });
        setEditingTable(table);
    };

    const handleOpenEditUser = (user) => {
        setUserForm({
            email: user.email,
            password: '',
            user_name: user.user_name || '',
            first_names: user.first_names || '',
            last_names: user.last_names || '',
            cell_phone: user.cell_phone || '',
            active: user.active,
            user_role: user.user_role || 2,
            pin: '',
            confirmPin: ''
        });
        setEditingUser(user);
        setChangePassword(false);
        setChangePin(false);
    };

    // Helper function to translate role titles
    const translateRoleTitle = (roleTitle) => {
        const roleTitleLower = roleTitle?.toLowerCase();
        switch(roleTitleLower) {
            case 'manager':
                return t('manager');
            case 'server':
                return t('server');
            case 'bar':
                return t('bartender');
            case 'owner':
                return t('owner');
            case 'kitchen':
                return t('chef');
            default:
                return roleTitle;
        }
    };

    // Component for tab navigation
    const TabBar = ({ tabs, activeTab, onTabChange }) => (
        <div
            role="tablist"
            aria-label={t('adminDashboardTabs') || 'Admin Dashboard Tabs'}
            style={{
                display: 'flex',
                background: theme?.colors?.surface || '#fff',
                borderBottom: `1px solid ${theme?.colors?.border || '#ccc'}`,
                marginBottom: '1.5rem',
                borderRadius: `${theme?.borderRadius || 8}px ${theme?.borderRadius || 8}px 0 0`,
                overflow: 'auto',
                boxShadow: theme?.boxShadow || '0 2px 4px rgba(0,0,0,0.1)'
            }}
        >
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    role="tab"
                    aria-selected={activeTab === tab.id}
                    aria-controls={`panel-${tab.id}`}
                    id={`tab-${tab.id}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onTabChange(tab.id);
                    }}
                    onTouchStart={(e) => {
                        e.stopPropagation();
                    }}
                    style={{
                        flex: '1 1 auto',
                        padding: '1rem 1.5rem',
                        background: activeTab === tab.id
                            ? (theme?.colors?.secondary || '#666')
                            : (theme?.colors?.surface || '#fff'),
                        color: activeTab === tab.id
                            ? (theme?.colors?.textLight || '#fff')
                            : (theme?.colors?.text || '#000'),
                        border: 'none',
                        borderBottom: activeTab === tab.id
                            ? `3px solid ${theme?.colors?.primary || '#1976d2'}`
                            : '3px solid transparent',
                        cursor: 'pointer',
                        fontWeight: activeTab === tab.id ? 'bold' : 'normal',
                        fontSize: '0.95rem',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease',
                        minWidth: 'fit-content',
                        WebkitTapHighlightColor: 'rgba(0,0,0,0)',
                        userSelect: 'none',
                        WebkitUserSelect: 'none'
                    }}
                    onMouseEnter={(e) => {
                        if (activeTab !== tab.id) {
                            e.currentTarget.style.background = theme?.colors?.background || '#f5f5f5';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (activeTab !== tab.id) {
                            e.currentTarget.style.background = theme?.colors?.surface || '#fff';
                        }
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '0.25rem',
                        pointerEvents: 'none'
                    }}>
                        <span style={{ fontSize: '1.2rem' }}>{tab.icon}</span>
                        <span>{t(tab.labelKey)}</span>
                    </div>
                </button>
            ))}
        </div>
    );

    // CollapsibleSection is defined outside the component to prevent remounting children on re-render

    // Map section IDs to their JSX content
    const renderSection = (sectionId) => {
        const sectionMap = {
            tablesManagement: {
                title: `🏛️ ${t('tablesManagement')}`, isOpen: showTablesManagement, onToggle: () => setShowTablesManagement(!showTablesManagement),
                content: <div style={{ padding: '0rem' }}><TablesManagement tables={tables} theme={theme} showDeletedTables={showDeletedTables} setShowDeletedTables={setShowDeletedTables} showAddTable={showAddTable} setShowAddTable={setShowAddTable} handleOpenEditTable={handleOpenEditTable} handleDeleteTable={handleDeleteTable} floorPlanSettings={floorPlanSettings} onSaveFloorPlanSettings={handleSaveFloorPlanSettings} areas={tableAreas} onAreasChanged={fetchTableAreas} /></div>
            },
            userManagement: {
                title: `👥 ${t('userManagement')}`, isOpen: showUsersManagement, onToggle: () => setShowUsersManagement(!showUsersManagement),
                content: <div style={{ padding: '0rem' }}><UsersManagement users={users} theme={theme} showInactiveUsers={showInactiveUsers} setShowInactiveUsers={setShowInactiveUsers} setShowAddUser={setShowAddUser} handleOpenEditUser={handleOpenEditUser} handleDeleteUser={handleDeleteUser} /></div>
            },
            activeSessionsManagement: {
                title: `👥 ${t('activeSessionsManagement')}`, isOpen: showActiveSessionsManagement, onToggle: () => setShowActiveSessionsManagement(!showActiveSessionsManagement),
                content: <div style={{ padding: '0rem' }}><ActiveSessionsManagement theme={theme} currentUser={user} /></div>
            },
            registerManagement: {
                title: `💰 ${t('registerManagement')}`, isOpen: showRegisterManagement, onToggle: () => setShowRegisterManagement(!showRegisterManagement),
                content: <div style={{ padding: '0rem' }}><RegisterManagement theme={theme} user={user} /></div>
            },
            productManagement: {
                title: `🛍️ ${t('productManagement')}`, isOpen: showProductManagement, onToggle: () => setShowProductManagement(!showProductManagement),
                content: <div style={{ padding: '0rem' }}><ProductManagement theme={theme} /></div>
            },
            optionManagement: {
                title: `⚙️ ${t('optionManagement')}`, isOpen: showOptionManagement, onToggle: () => setShowOptionManagement(!showOptionManagement),
                content: <div style={{ padding: '0rem' }}><OptionManagement theme={theme} /></div>
            },
            specialsManagement: {
                title: `🎁 ${t('specialsManagement')}`, isOpen: showSpecialsManagement, onToggle: () => setShowSpecialsManagement(!showSpecialsManagement),
                content: <div style={{ padding: '0rem' }}><SpecialsManagement theme={theme} /></div>
            },
            tipAnalysis: {
                title: `💳 ${t('creditTips')} ${t('analysis')}`, isOpen: showTipAnalysis, onToggle: () => setShowTipAnalysis(!showTipAnalysis),
                content: <div style={{ padding: '1.5rem' }}><button onClick={() => setShowTipAnalysis(true)} style={{ backgroundColor: theme.colors.primary, color: theme.colors.surface, border: 'none', borderRadius: theme.borderRadius || 4, padding: '1rem 2rem', cursor: 'pointer', fontSize: '1.1rem', fontWeight: 'bold', width: '100%' }}>{t('import')} CSV {t('and')} {t('analyze')} {t('tips')}</button></div>
            },
            outboundInvoices: {
                title: `📤 ${t('outboundInvoices')}`, isOpen: showInvoiceHistory, onToggle: () => setShowInvoiceHistory(!showInvoiceHistory),
                content: <div style={{ padding: '0rem' }}><OutboundInvoices theme={theme} onCreateInvoice={(orderId) => { setSelectedOrderForInvoice(orderId); setShowInvoiceGenerator(true); }} /></div>
            },
            inboundInvoices: {
                title: `📥 ${t('inboundInvoices')}`, isOpen: showInboundInvoices, onToggle: () => setShowInboundInvoices(!showInboundInvoices),
                content: <div style={{ padding: '0rem' }}><InboundInvoices theme={theme} /></div>
            },
            beerProduction: {
                title: `🍺 ${t('beerProduction')}`, isOpen: showBeerProduction, onToggle: () => setShowBeerProduction(!showBeerProduction),
                content: <div style={{ padding: '0rem' }}><BeerProduction theme={theme} /></div>
            },
            inventoryManagement: {
                title: `📦 ${t('inventoryManagement')}`, isOpen: showInventoryManagement, onToggle: () => setShowInventoryManagement(!showInventoryManagement),
                content: <div style={{ padding: '1.5rem' }}><InventoryManagement theme={theme} /></div>
            },
            iepsCalculator: {
                title: `📊 ${t('iepsCalculator')} - ${t('cervezasGibbons')}`, isOpen: showIEPSCalculator, onToggle: () => setShowIEPSCalculator(!showIEPSCalculator),
                content: <div style={{ padding: '1.5rem' }}><IEPSCalculator theme={theme} /></div>
            },
            quickStats: {
                title: `💰 ${t('sales')}`, isOpen: showQuickStats, onToggle: () => setShowQuickStats(!showQuickStats),
                content: <QuickViewStats stats={stats} theme={theme} showOpenTabs={showOpenTabs} setShowOpenTabs={setShowOpenTabs} showClosedTabs={showClosedTabs} setShowClosedTabs={setShowClosedTabs} showTotalTabs={showTotalTabs} setShowTotalTabs={setShowTotalTabs} showCashTabs={showCashTabs} setShowCashTabs={setShowCashTabs} showCreditTabs={showCreditTabs} setShowCreditTabs={setShowCreditTabs} showOtherTabs={showOtherTabs} setShowOtherTabs={setShowOtherTabs} showAverageTimesTabs={showAverageTimesTabs} setShowAverageTimesTabs={setShowAverageTimesTabs} openOrders={openOrders} closedOrders={closedOrders} totalOrders={totalOrders} cashOrders={cashOrders} creditOrders={creditOrders} otherOrders={otherOrders} timePeriod={timePeriod} setTimePeriod={setTimePeriod} selectedDate={selectedDate} setSelectedDate={setSelectedDate} selectedHour={selectedHour} setSelectedHour={setSelectedHour} averageTimesByArea={averageTimesByArea} onDataRefresh={fetchAdminData} onCreateInvoice={(orderId) => { setSelectedOrderForInvoice(orderId); setShowInvoiceGenerator(true); }} user={user} />
            },
            advancedAnalytics: {
                title: `📊 ${t('advancedAnalytics')}`, isOpen: showAdvancedAnalytics, onToggle: () => setShowAdvancedAnalytics(!showAdvancedAnalytics),
                content: <AdvancedAnalytics theme={theme} />
            },
            digitalMenu: {
                title: null, // No CollapsibleSection wrapper
                content: <DigitalMenuSettings theme={theme} onBrandingChange={onBrandingChange} />
            },
            systemSettings: {
                title: `⚙️ ${t('systemSettings')}`, isOpen: showSystemSettings, onToggle: () => setShowSystemSettings(!showSystemSettings),
                content: <div style={{ padding: '0rem' }}><SystemSettings theme={theme} user={user} /></div>
            },
            adminLayout: {
                title: `🧩 ${t('adminLayout') || 'Admin Layout'}`, isOpen: showAdminLayout, onToggle: () => setShowAdminLayout(!showAdminLayout),
                content: <AdminLayoutConfigurator theme={theme} layout={adminLayout} onLayoutChange={setAdminLayout} />
            },
            printerManagement: {
                title: `🖨️ ${t('printerManagement')}`, isOpen: showPrinterManagement, onToggle: () => setShowPrinterManagement(!showPrinterManagement),
                content: <div style={{ padding: '1.5rem' }}><PrinterManagement theme={theme} /></div>
            },
            themeManagement: {
                title: `🎨 ${t('themeManagement')}`, isOpen: showThemeManagement, onToggle: () => setShowThemeManagement(!showThemeManagement),
                content: <div style={{ padding: '1.5rem' }}><ThemeManagement theme={theme} onThemeChange={onThemeChange} onBrandingChange={onBrandingChange} /></div>
            }
        };

        const section = sectionMap[sectionId];
        if (!section) return null;

        // Sections without a title render directly (e.g., digitalMenu)
        if (section.title === null) return section.content;

        return (
            <CollapsibleSection
                key={sectionId}
                title={section.title}
                isOpen={section.isOpen}
                onToggle={section.onToggle}
                theme={theme}
            >
                {section.content}
            </CollapsibleSection>
        );
    };

    // Render content based on active tab - data-driven from layout
    const renderTabContent = () => {
        const tabConfig = adminLayout.tabs.find(tab => tab.id === activeTab);
        if (!tabConfig) return null;

        const visibleSections = tabConfig.sections.filter(s => s.visible);
        if (visibleSections.length === 0) return null;

        return (
            <>
                {visibleSections.map(section => (
                    <div key={section.id}>{renderSection(section.id)}</div>
                ))}
            </>
        );
    };

    if (loading) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: theme.colors.textSecondary
            }}>
                Loading admin data...
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                textAlign: 'center',
                padding: '2rem',
                color: 'red'
            }}>
                Error loading admin data: {error}
            </div>
        );
    }

    return (
        <div style={{
            padding: '.5rem',
            background: theme?.colors?.background || '#f5f5f5',
            color: theme?.colors?.text || '#000',
            minHeight: '80vh'
        }}>
            {/* <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '2rem'
            }}>
                <h1 style={{
                    color: theme.colors.primary,
                    margin: 0
                }}>
                    🛠️ Admin Dashboard
                </h1>
            </div> */}

            {/* Tab Navigation */}
            <TabBar
                tabs={TAB_CONFIG}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />

            {/* Tab Content */}
            <div
                role="tabpanel"
                id={`panel-${activeTab}`}
                aria-labelledby={`tab-${activeTab}`}
                style={{
                    background: theme?.colors?.background || '#f5f5f5',
                    color: theme?.colors?.text || '#000',
                    minHeight: '50vh',
                    padding: '0.5rem 0'
                }}
            >
                {renderTabContent()}
            </div>

            {/* Add Table Modal */}
            {showAddTable && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '90%'
                    }}>
                        <h3 style={{ color: theme.colors.primary, marginTop: 0 }}>Add New Table</h3>

                        {/* Top Button Section */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '1rem',
                            marginBottom: '1rem'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddTable(false);
                                    setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme?.colors?.error || '#f44336',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTable}
                                disabled={!tableForm.name}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !tableForm.name ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: !tableForm.name ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !tableForm.name ? 0.6 : 1
                                }}
                            >
                                Add Table
                            </button>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                Table Name:
                            </label>
                            <input
                                type="text"
                                value={tableForm.name}
                                onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder="Enter table name"
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                Shape:
                            </label>
                            <select
                                value={tableForm.shape}
                                onChange={(e) => setTableForm({ ...tableForm, shape: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="rectangle">Rectangle</option>
                                <option value="round">Round</option>
                                <option value="square">Square</option>
                            </select>
                        </div>

                        {tableAreasEnabled && tableAreas.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    {t('area')}:
                                </label>
                                <select
                                    value={tableForm.area_id || 1}
                                    onChange={(e) => setTableForm({ ...tableForm, area_id: Number(e.target.value) })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                >
                                    {tableAreas.map(area => (
                                        <option key={area.id} value={area.id}>{area.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    Width:
                                </label>
                                <input
                                    type="number"
                                    value={tableForm.width}
                                    onChange={(e) => setTableForm({ ...tableForm, width: Number(e.target.value) })}
                                    onWheel={(e) => e.target.blur()}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    Height:
                                </label>
                                <input
                                    type="number"
                                    value={tableForm.height}
                                    onChange={(e) => setTableForm({ ...tableForm, height: Number(e.target.value) })}
                                    onWheel={(e) => e.target.blur()}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                        </div>

                        {/* Bottom Button Section */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddTable(false);
                                    setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme?.colors?.error || '#f44336',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddTable}
                                disabled={!tableForm.name}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !tableForm.name ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: !tableForm.name ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !tableForm.name ? 0.6 : 1
                                }}
                            >
                                Add Table
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Table Modal */}
            {editingTable && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '90%'
                    }}>
                        <h3 style={{ color: theme.colors.primary, marginTop: 0 }}>Edit Table</h3>

                        {/* Top Button Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
                            <button
                                onClick={() => {
                                    setEditingTable(null);
                                    setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme.colors.error || '#f44336',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditTable}
                                disabled={!tableForm.name}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !tableForm.name ? '#ccc' : theme.colors.success || '#4caf50',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: !tableForm.name ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !tableForm.name ? 0.6 : 1
                                }}
                            >
                                Update Table
                            </button>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                Table Name:
                            </label>
                            <input
                                type="text"
                                value={tableForm.name}
                                onChange={(e) => setTableForm({ ...tableForm, name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                Shape:
                            </label>
                            <select
                                value={tableForm.shape}
                                onChange={(e) => setTableForm({ ...tableForm, shape: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value="rectangle">Rectangle</option>
                                <option value="round">Round</option>
                                <option value="square">Square</option>
                            </select>
                        </div>

                        {tableAreasEnabled && tableAreas.length > 0 && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    {t('area')}:
                                </label>
                                <select
                                    value={tableForm.area_id || 1}
                                    onChange={(e) => setTableForm({ ...tableForm, area_id: Number(e.target.value) })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                >
                                    {tableAreas.map(area => (
                                        <option key={area.id} value={area.id}>{area.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    Width:
                                </label>
                                <input
                                    type="number"
                                    value={tableForm.width}
                                    onChange={(e) => setTableForm({ ...tableForm, width: Number(e.target.value) })}
                                    onWheel={(e) => e.target.blur()}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                    Height:
                                </label>
                                <input
                                    type="number"
                                    value={tableForm.height}
                                    onChange={(e) => setTableForm({ ...tableForm, height: Number(e.target.value) })}
                                    onWheel={(e) => e.target.blur()}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                />
                            </div>
                        </div>

                        {editingTable?.deleted && (
                            <div style={{ marginBottom: '1rem' }}>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    fontSize: '0.9rem',
                                    color: theme.colors.text,
                                    cursor: 'pointer'
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={!tableForm.deleted}
                                        onChange={(e) => setTableForm({ ...tableForm, deleted: !e.target.checked })}
                                        style={{ marginRight: '0.5rem' }}
                                    />
                                    Restore this table (undelete)
                                </label>
                            </div>
                        )}

                        {/* Bottom Button Section */}
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setEditingTable(null);
                                    setTableForm({ name: '', shape: 'rectangle', width: 80, height: 50, area_id: 1 });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme.colors.error || '#f44336',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleEditTable}
                                disabled={!tableForm.name}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !tableForm.name ? '#ccc' : theme.colors.success || '#4caf50',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: !tableForm.name ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !tableForm.name ? 0.6 : 1
                                }}
                            >
                                Update Table
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {showAddUser && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ color: theme.colors.primary, marginTop: 0 }}>{t('addNewUser')}</h3>

                        {/* Top Button Section */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: '1rem',
                            marginBottom: '1rem'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddUser(false);
                                    setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', cell_phone: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme?.colors?.error || '#f44336',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleAddUser}
                                disabled={!userForm.email || !userForm.password}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: (!userForm.email || !userForm.password) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: (!userForm.email || !userForm.password) ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: (!userForm.email || !userForm.password) ? 0.6 : 1
                                }}
                            >
                                {t('addUser')}
                            </button>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('email')}: *
                            </label>
                            <input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('emailPlaceholder')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('password')}: *
                            </label>
                            <input
                                type="password"
                                value={userForm.password}
                                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('password')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('username')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.user_name}
                                onChange={(e) => setUserForm({ ...userForm, user_name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('username')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('firstNames')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.first_names}
                                onChange={(e) => setUserForm({ ...userForm, first_names: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('firstNames')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('lastNames')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.last_names}
                                onChange={(e) => setUserForm({ ...userForm, last_names: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('lastNames')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('phone')}:
                            </label>
                            <input
                                type="tel"
                                value={userForm.cell_phone}
                                onChange={(e) => setUserForm({ ...userForm, cell_phone: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('phonePlaceholder')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('role')}:
                            </label>
                            <select
                                value={userForm.user_role}
                                onChange={(e) => setUserForm({ ...userForm, user_role: Number(e.target.value) })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                {userRoles.map(role => (
                                    <option key={role.id} value={role.id}>{translateRoleTitle(role.title)}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('status')}:
                            </label>
                            <select
                                value={userForm.active}
                                onChange={(e) => setUserForm({ ...userForm, active: Number(e.target.value) })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value={1}>{t('active')}</option>
                                <option value={0}>{t('inactive')}</option>
                            </select>
                        </div>

                        {/* Bottom Button Section */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem'
                        }}>
                            <button
                                onClick={() => {
                                    setShowAddUser(false);
                                    setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', cell_phone: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme?.colors?.error || '#f44336',
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleAddUser}
                                disabled={!userForm.email || !userForm.password}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: (!userForm.email || !userForm.password) ? '#ccc' : (theme?.colors?.success || '#4caf50'),
                                    color: theme?.colors?.surface || '#fff',
                                    border: 'none',
                                    borderRadius: theme?.borderRadius || 4,
                                    cursor: (!userForm.email || !userForm.password) ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: (!userForm.email || !userForm.password) ? 0.6 : 1
                                }}
                            >
                                {t('addUser')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: theme.colors.surface,
                        borderRadius: theme.borderRadius || 8,
                        padding: '2rem',
                        maxWidth: '400px',
                        width: '90%',
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <h3 style={{ color: theme.colors.primary, marginTop: 0 }}>{t('editUser')}</h3>

                        {/* Top Button Section */}
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '1rem' }}>
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
                                    setChangePassword(false);
                                    setChangePin(false);
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme.colors.error || '#f44336',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleEditUser}
                                disabled={!userForm.email}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !userForm.email ? '#ccc' : theme.colors.success || '#4caf50',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: !userForm.email ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !userForm.email ? 0.6 : 1
                                }}
                            >
                                {t('updateUser')}
                            </button>
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('email')}: *
                            </label>
                            <input
                                type="email"
                                value={userForm.email}
                                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('username')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.user_name}
                                onChange={(e) => setUserForm({ ...userForm, user_name: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                cursor: 'pointer',
                                marginBottom: '0.5rem'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={changePassword}
                                    onChange={(e) => setChangePassword(e.target.checked)}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                {t('changePassword')}
                            </label>
                            {changePassword && (
                                <input
                                    type="password"
                                    value={userForm.password}
                                    onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                                    style={{
                                        width: '100%',
                                        padding: '0.5rem',
                                        borderRadius: theme.borderRadius || 4,
                                        border: '1px solid #ddd',
                                        fontSize: '1rem'
                                    }}
                                    placeholder={t('enterNewPassword')}
                                />
                            )}
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{
                                display: 'flex',
                                alignItems: 'center',
                                fontSize: '0.9rem',
                                color: theme.colors.text,
                                cursor: 'pointer',
                                marginBottom: '0.5rem'
                            }}>
                                <input
                                    type="checkbox"
                                    checked={changePin}
                                    onChange={(e) => {
                                        setChangePin(e.target.checked);
                                        if (!e.target.checked) {
                                            setUserForm({ ...userForm, pin: '', confirmPin: '' });
                                        }
                                    }}
                                    style={{ marginRight: '0.5rem' }}
                                />
                                {t('changePin') || 'Change PIN'}
                            </label>
                            {changePin && (
                                <div>
                                    <input
                                        type="password"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={4}
                                        value={userForm.pin}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setUserForm({ ...userForm, pin: value });
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            borderRadius: theme.borderRadius || 4,
                                            border: '1px solid #ddd',
                                            fontSize: '1rem',
                                            marginBottom: '0.5rem'
                                        }}
                                        placeholder={t('enterPin')}
                                    />
                                    <input
                                        type="password"
                                        inputMode="numeric"
                                        pattern="[0-9]*"
                                        maxLength={4}
                                        value={userForm.confirmPin}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setUserForm({ ...userForm, confirmPin: value });
                                        }}
                                        style={{
                                            width: '100%',
                                            padding: '0.5rem',
                                            borderRadius: theme.borderRadius || 4,
                                            border: '1px solid #ddd',
                                            fontSize: '1rem'
                                        }}
                                        placeholder={t('confirmPin')}
                                    />
                                </div>
                            )}
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('firstNames')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.first_names}
                                onChange={(e) => setUserForm({ ...userForm, first_names: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('lastNames')}:
                            </label>
                            <input
                                type="text"
                                value={userForm.last_names}
                                onChange={(e) => setUserForm({ ...userForm, last_names: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('phone')}:
                            </label>
                            <input
                                type="tel"
                                value={userForm.cell_phone}
                                onChange={(e) => setUserForm({ ...userForm, cell_phone: e.target.value })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                                placeholder={t('phonePlaceholder')}
                            />
                        </div>

                        <div style={{ marginBottom: '1rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('role')}:
                            </label>
                            <select
                                value={userForm.user_role}
                                onChange={(e) => setUserForm({ ...userForm, user_role: Number(e.target.value) })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                {userRoles.map(role => (
                                    <option key={role.id} value={role.id}>{translateRoleTitle(role.title)}</option>
                                ))}
                            </select>
                        </div>

                        <div style={{ marginBottom: '1.5rem' }}>
                            <label style={{ display: 'block', marginBottom: '0.5rem', color: theme.colors.text }}>
                                {t('status')}:
                            </label>
                            <select
                                value={userForm.active}
                                onChange={(e) => setUserForm({ ...userForm, active: Number(e.target.value) })}
                                style={{
                                    width: '100%',
                                    padding: '0.5rem',
                                    borderRadius: theme.borderRadius || 4,
                                    border: '1px solid #ddd',
                                    fontSize: '1rem'
                                }}
                            >
                                <option value={1}>{t('active')}</option>
                                <option value={0}>{t('inactive')}</option>
                            </select>
                        </div>

                        {/* Bottom Button Section */}
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setUserForm({ email: '', password: '', user_name: '', first_names: '', last_names: '', active: 1, user_role: 2, pin: '', confirmPin: '' });
                                    setChangePassword(false);
                                    setChangePin(false);
                                }}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: theme.colors.error || '#f44336',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold'
                                }}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                onClick={handleEditUser}
                                disabled={!userForm.email}
                                style={{
                                    padding: '0.75rem 1.5rem',
                                    backgroundColor: !userForm.email ? '#ccc' : theme.colors.success || '#4caf50',
                                    color: theme.colors.surface,
                                    border: 'none',
                                    borderRadius: theme.borderRadius || 4,
                                    cursor: !userForm.email ? 'not-allowed' : 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: 'bold',
                                    opacity: !userForm.email ? 0.6 : 1
                                }}
                            >
                                {t('updateUser')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tip Analysis Modal */}
            <TipAnalysisModal
                isOpen={showTipAnalysis}
                onClose={() => setShowTipAnalysis(false)}
            />

            {/* Invoice Generator Modal */}
            {showInvoiceGenerator && selectedOrderForInvoice && (
                <InvoiceGenerator
                    orderId={selectedOrderForInvoice}
                    onClose={() => {
                        setShowInvoiceGenerator(false);
                        setSelectedOrderForInvoice(null);
                    }}
                />
            )}

        </div>
    );
}